import { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "@/hooks/storeHooks.ts";
import { RADIO_OPTIONS, Variant } from "../../utils/enums.js";
import { FaArrowRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  keywordOptions,
  filterAssignCombinations
} from "../../utils/formUtility.js";
import {
  clearFormStateAction,
  setFormStateAction
} from "../../actions/formActions.ts";
import "../../css/form.css";
import "./FAQFilter.css";
import {
  IFAQAll,
  IKeywordValueLabel,
  valueLabelPair
} from "@/types/SPTypes.tsx";
import { useForm, useWatch } from "react-hook-form";
import { isIFAQGuest, isISectionGuest } from "@/types/typeGuards.ts";
import { isEqual } from "lodash";
import {
  keywordTooltip,
  systemRadioTooltip,
  operationRadioTooltip,
  allSectionsRadioTooltip,
  showConnectedSectionsTooltip,
  questionTypeTooltip,
  sectionTooltip,
  clientTypeTooltip,
  searchTooltip
} from "@/utils/tooltipTexts.tsx";
import {
  ButtonComponent,
  CheckboxComponent,
  InputComponent,
  RadioComponent,
  SelectComponent,
  TooltipComponent
} from "fmv-component-ui-library";

export interface FAQFilterFormValues {
  clientType: valueLabelPair<number> | null;
  keyword: IKeywordValueLabel | null;
  section: valueLabelPair<number> | null;
  questionType: valueLabelPair<number> | null;
  selectedRadioOption: string;
  searchText: string;
  showConnectedSections: boolean;
}

const FAQFilter = ({
  FAQData,
  handleFAQFilterChange
}: {
  FAQData: IFAQAll[];
  handleFAQFilterChange: (updatedFAQs: IFAQAll[]) => void;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const data = useAppSelector(state => state.application);
  const isLoggedIn = useAppSelector(state => state.application.isLoggedIn);
  const existingFormValues = useAppSelector(state => state.form);

  const initialFormState = {
    clientType: existingFormValues.clientType || null,
    section: existingFormValues.section || null,
    questionType: existingFormValues.questionType || null,
    keyword: existingFormValues.keyword || null,
    selectedRadioOption:
      existingFormValues.selectedRadioOption || RADIO_OPTIONS.ALL,
    searchText: existingFormValues.searchText || "",
    showConnectedSections: existingFormValues.showConnectedSections || false
  };

  const { control, reset, setValue } = useForm<FAQFilterFormValues>({
    mode: "onChange",
    defaultValues: initialFormState
  });

  const clientType = useWatch({ control, name: "clientType" });
  const section = useWatch({ control, name: "section" });
  const questionType = useWatch({ control, name: "questionType" });
  const searchText = useWatch({ control, name: "searchText" });
  const showConnectedSections = useWatch({
    control,
    name: "showConnectedSections"
  });
  const keyword = useWatch({ control, name: "keyword" });
  const selectedRadioOption = useWatch({
    control,
    name: "selectedRadioOption"
  });

  const [filteredFAQs, setFilteredFAQs] = useState<IFAQAll[]>([]);

  const getUniqueFAQs = (FAQs: IFAQAll[]) => {
    const seen = new Set();
    return FAQs.filter((FAQ: IFAQAll) => !seen.has(FAQ.ID) && seen.add(FAQ.ID));
  };

  const [sectionOptions, questionTypeOptions, clientTypeOptions] =
    filterAssignCombinations(
      section,
      clientType,
      questionType,
      selectedRadioOption,
      data
    );

  useEffect(() => {
    let updatedFAQs = [...FAQData];

    if (clientType) {
      updatedFAQs = updatedFAQs.filter(FAQ =>
        isIFAQGuest(FAQ)
          ? FAQ.clientTypesTitle === clientType.label
          : FAQ.clientTypes[0]?.ID === clientType.value
      );
    }

    if (section) {
      updatedFAQs = updatedFAQs.filter(FAQ =>
        isLoggedIn
          ? FAQ.section?.ID === section.value
          : FAQ.sectionTitle === section.label
      );
    }

    if (questionType) {
      updatedFAQs = updatedFAQs.filter(FAQ =>
        isLoggedIn
          ? FAQ.questionType?.ID === questionType.value
          : FAQ.questionTypeTitle === questionType.label
      );
    }

    if (searchText) {
      const text = searchText.toLowerCase();
      updatedFAQs = updatedFAQs.filter(FAQ =>
        [FAQ.FAQTitle, FAQ.question, FAQ.answer, FAQ.ID.toString()].some(
          field => field && field.toLowerCase().includes(text)
        )
      );
    }

    if (showConnectedSections && section) {
      const fullSection = data.sections.find(
        connectedSection => connectedSection.sectionTitle === section.label
      );
      if (isLoggedIn) {
        const connected = fullSection?.connectedSections ?? [];
        const connectedFAQs = FAQData.filter(FAQ =>
          connected.some(
            connectedSection =>
              connectedSection.sectionTitle === FAQ.section?.sectionTitle
          )
        );
        updatedFAQs = getUniqueFAQs([...updatedFAQs, ...connectedFAQs]);
      } else {
        if (isISectionGuest(fullSection)) {
          const connectedTitles =
            fullSection.connectedSectionsTitles?.split(", ") ?? [];
          const connectedFAQs = FAQData.filter(FAQ =>
            connectedTitles.includes(FAQ.sectionTitle)
          );
          updatedFAQs = getUniqueFAQs([...updatedFAQs, ...connectedFAQs]);
        }
      }
    }
    setFilteredFAQs(updatedFAQs);
    handleFAQFilterChange(updatedFAQs);
  }, [
    FAQData,
    clientType,
    section,
    questionType,
    searchText,
    showConnectedSections,
    isLoggedIn,
    data.sections,
    keyword
  ]);

  useEffect(() => {
    if (!isEqual(clientType, existingFormValues.clientType)) {
      dispatch(setFormStateAction({ ...existingFormValues, clientType }));
    }
  }, [clientType]);

  useEffect(() => {
    if (!isEqual(section, existingFormValues.section)) {
      dispatch(setFormStateAction({ ...existingFormValues, section }));
    }
  }, [section]);

  useEffect(() => {
    if (!isEqual(questionType, existingFormValues.questionType)) {
      dispatch(setFormStateAction({ ...existingFormValues, questionType }));
    }
  }, [questionType]);

  useEffect(() => {
    if (!isEqual(searchText, existingFormValues.searchText)) {
      dispatch(setFormStateAction({ ...existingFormValues, searchText }));
    }
  }, [searchText]);

  useEffect(() => {
    if (
      !isEqual(showConnectedSections, existingFormValues.showConnectedSections)
    ) {
      dispatch(
        setFormStateAction({ ...existingFormValues, showConnectedSections })
      );
    }
  }, [showConnectedSections]);

  useEffect(() => {
    if (!isEqual(selectedRadioOption, existingFormValues.selectedRadioOption)) {
      dispatch(
        setFormStateAction({ ...existingFormValues, selectedRadioOption })
      );
    }
  }, [selectedRadioOption]);

  useEffect(() => {
    if (!isEqual(keyword, existingFormValues.keyword)) {
      dispatch(setFormStateAction({ ...existingFormValues, keyword }));
    }
  }, [keyword]);

  useEffect(() => {
    // set section based on keyword
    if (keyword) {
      const sectionValue = isLoggedIn ? keyword.section?.ID : keyword.value;
      const sectionLabel = isLoggedIn
        ? keyword.section?.sectionTitle
        : keyword.sectionTitle;

      const newSection =
        sectionValue !== undefined && sectionLabel !== undefined
          ? { value: sectionValue, label: sectionLabel }
          : null;

      // only update form if section is different
      if (newSection && !isEqual(newSection, section)) {
        setValue("section", newSection);
      }
    }
  }, [keyword, isLoggedIn, section, setValue]);

  return (
    <div>
      <div className="FAQ-filter-row">
        <TooltipComponent content={questionTypeTooltip} placement="top">
          {" "}
          <SelectComponent
            name="questionType"
            control={control}
            label="Min fråga handlar om:"
            options={questionTypeOptions}
            isClearable
          />
        </TooltipComponent>
        <TooltipComponent content={clientTypeTooltip} placement="top">
          {" "}
          <SelectComponent
            name="clientType"
            control={control}
            label="Jag tillhör"
            options={clientTypeOptions}
            isClearable
          />
        </TooltipComponent>
      </div>
      <div className="FAQ-filter-row">
        <div>
          <TooltipComponent content={sectionTooltip} placement="top">
            <SelectComponent
              name="section"
              control={control}
              label="Vilket tjänsteområde eller tekniskt system gäller det?"
              options={sectionOptions}
              isClearable
            />
          </TooltipComponent>
          <div className="radio-btn-container">
            <TooltipComponent content={systemRadioTooltip} placement="top">
              {" "}
              <RadioComponent
                label="Tekniskt system"
                name="selectedRadioOption"
                value={RADIO_OPTIONS.SYSTEM}
                control={control}
              />
            </TooltipComponent>
            <TooltipComponent content={operationRadioTooltip} placement="top">
              {" "}
              <RadioComponent
                label="Tjänsteområde"
                name="selectedRadioOption"
                value={RADIO_OPTIONS.OPERATION}
                control={control}
              />
            </TooltipComponent>
            <TooltipComponent content={allSectionsRadioTooltip} placement="top">
              {" "}
              <RadioComponent
                label="Alla"
                name="selectedRadioOption"
                value={RADIO_OPTIONS.ALL}
                control={control}
              />
            </TooltipComponent>
          </div>
          <div className="connected-sections-checkbox">
            <TooltipComponent
              content={showConnectedSectionsTooltip}
              placement="top"
            >
              <CheckboxComponent
                name="showConnectedSections"
                control={control}
                label="Visa kopplade"
              />
            </TooltipComponent>
          </div>
        </div>
        <TooltipComponent content={keywordTooltip} placement="top">
          <SelectComponent
            name="keyword"
            control={control}
            label="Nyckelord"
            options={keywordOptions(data.keywords) || []}
            isClearable
          />
        </TooltipComponent>
      </div>
      <div className="FAQ-filter-row">
        <TooltipComponent content={searchTooltip} placement="top">
          <InputComponent
            control={control}
            name="searchText"
            label="Sök..."
            placeholder="Sök..."
          />
        </TooltipComponent>
      </div>
      <div className="clear-inputs-row">
        <a
          onClick={() => {
            reset();
            dispatch(clearFormStateAction());
          }}
        >
          Rensa
        </a>
      </div>{" "}
      <div className="results-count-container">
        {filteredFAQs && (
          <p>
            <b>{filteredFAQs.length}</b> FAQ:er visas
          </p>
        )}
      </div>
      <div className="faq-form-btn-container">
        <ButtonComponent
          variant={Variant.PRIMARY}
          onClick={() => {
            navigate("/skapa-arende");
          }}
          label="Gå till skapa ärende"
          rightIcon={<FaArrowRight />}
          size="lg"
        />
      </div>
    </div>
  );
};
export default FAQFilter;
